import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import '../design-system.css';
import './AdminLenderPreCheck.css';

interface LenderResult {
  original_lender: string;
  matched_lender: string;
  email?: string;
  source: string;
  confidence?: number;
  claim_count: number;
  claim_ids: string[];
  status: 'matched' | 'no_match' | 'error';
  error?: string;
  queued_for_review?: boolean;
}

interface PreCheckResults {
  total_claims: number;
  unique_lenders: number;
  lender_results: LenderResult[];
  summary: {
    fuzzy_matched: number;
    claude_matched: number;
    cached: number;
    no_match: number;
    errors: number;
    queued_suggestions?: number;
  };
  queued_suggestions?: Array<{
    original: string;
    matched?: string;
    email?: string;
    confidence?: number;
    reasoning?: string;
    timestamp: string;
  }>;
  recommendations: Array<{
    type: 'warning' | 'error' | 'info' | 'success';
    message: string;
  }>;
}

export const AdminLenderPreCheck: React.FC = () => {
  const { apiRequest } = useAuth();
  const [isChecking, setIsChecking] = useState(false);
  const [results, setResults] = useState<PreCheckResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePreCheck = async () => {
    setIsChecking(true);
    setError(null);
    setResults(null);

    try {
      const data = await apiRequest({
        method: 'POST',
        url: '/ui/admin/preprocess-lenders',
        data: {
          limit: 0,
          previewOnly: true
        }
      }) as any;

      // Adapt the new structure to existing view model if needed
      const queuedSuggestions = data.queued_suggestions ?? data.auto_added_lenders ?? [];

      const adapted: PreCheckResults = {
        total_claims: data.totalClaims ?? data.total_claims ?? 0,
        unique_lenders: (data.canonical + data.alias + data.fuzzy + data.claude + data.unknown) ?? 0,
        lender_results: [], // backend not providing per-lender list in new endpoint yet
        summary: {
          fuzzy_matched: data.fuzzy ?? 0,
          claude_matched: data.claude ?? 0,
          cached: 0,
          no_match: data.unknown ?? 0,
          errors: data.errors ?? 0,
          queued_suggestions: data.summary?.queued_suggestions ?? data.auto_added_to_csv ?? queuedSuggestions.length ?? 0,
        },
        queued_suggestions: queuedSuggestions,
        recommendations: [],
      } as any;

      setResults(adapted);
    } catch (err: any) {
      console.error('Pre-check error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to check lenders');
    } finally {
      setIsChecking(false);
    }
  };

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'fuzzy':
        return <span className="badge badge-success">Fuzzy Match</span>;
      case 'claude':
        return <span className="badge badge-info">Claude AI</span>;
      case 'cache':
        return <span className="badge badge-secondary">Cached</span>;
      case 'error':
        return <span className="badge badge-danger">Error</span>;
      default:
        return <span className="badge">{source}</span>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'matched':
        return '✓';
      case 'no_match':
        return '⚠';
      case 'error':
        return '✗';
      default:
        return '?';
    }
  };

  return (
    <div className="admin-section">
      <h3>Pre-Check Lender Matches</h3>
      <p className="text-muted">
        Test lender matching for all claims without LOCs sent. This will check each unique lender 
        and cache the results before you send any LOCs.
      </p>

      <div className="alert alert-info mt-3">
        <strong>Note:</strong> This process uses AI matching with rate limiting (10 requests/minute).
        First run may take several minutes. Subsequent runs will be faster due to caching.
      </div>

      <button
        className="button button-primary"
        onClick={handlePreCheck}
        disabled={isChecking}
      >
        {isChecking ? 'Checking Lenders... (this may take a few minutes)' : 'Pre-Check All Lenders'}
      </button>

      {isChecking && (
        <div className="mt-3">
          <div className="alert alert-warning">
            <strong>Processing...</strong> The lender check is running in the background. 
            This can take 3-5 minutes for the first run. Please be patient.
            <br />
            <small>The system is checking each unique lender and caching results for future use.</small>
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-error mt-3">
          Error: {error}
        </div>
      )}

      {results && (
        <div className="mt-4">
          <div className="summary-cards">
            <div className="card">
              <h4>Summary</h4>
              <div className="stats-grid">
                <div>
                  <strong>{results.total_claims}</strong>
                  <span>Total Claims</span>
                </div>
                <div>
                  <strong>{results.unique_lenders}</strong>
                  <span>Unique Lenders</span>
                </div>
              </div>
            </div>

            <div className="card">
              <h4>Match Results</h4>
              <div className="stats-grid">
                <div className="text-success">
                  <strong>{results.summary.fuzzy_matched}</strong>
                  <span>Fuzzy Matched</span>
                </div>
                <div className="text-info">
                  <strong>{results.summary.claude_matched}</strong>
                  <span>Claude Matched</span>
                </div>
                <div className="text-secondary">
                  <strong>{results.summary.cached}</strong>
                  <span>From Cache</span>
                </div>
                <div className="text-warning">
                  <strong>{results.summary.no_match}</strong>
                  <span>No Match</span>
                </div>
                <div className="text-danger">
                  <strong>{results.summary.errors}</strong>
                  <span>Errors</span>
                </div>
              </div>
            </div>
            
            {results.summary.queued_suggestions && results.summary.queued_suggestions > 0 && (
              <div className="card">
                <h4>Claude Suggestions</h4>
                <div className="stats-grid">
                  <div className="text-primary">
                    <strong>{results.summary.queued_suggestions}</strong>
                    <span>Queued for review</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {results.recommendations.length > 0 && (
            <div className="recommendations mt-3">
              <h4>Recommendations</h4>
              {results.recommendations.map((rec, idx) => (
                <div key={idx} className={`alert alert-${rec.type}`}>
                  {rec.message}
                </div>
              ))}
            </div>
          )}

          <div className="lender-results mt-4">
            <h4>Lender Match Details</h4>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Original Lender</th>
                    <th>Matched To</th>
                    <th>Source</th>
                    <th>Claims</th>
                    <th>Sample IDs</th>
                  </tr>
                </thead>
                <tbody>
                  {results.lender_results.map((result, idx) => (
                    <tr key={idx} className={result.status === 'no_match' ? 'table-warning' : ''}>
                      <td>{getStatusIcon(result.status)}</td>
                      <td>{result.original_lender}</td>
                      <td>
                        {result.matched_lender}
                        {result.confidence && (
                          <span className="text-muted ml-2">
                            ({Math.round(result.confidence * 100)}%)
                          </span>
                        )}
                        {result.queued_for_review && (
                          <span className="badge badge-info ml-2">Queued</span>
                        )}
                      </td>
                      <td>{getSourceBadge(result.source)}</td>
                      <td>{result.claim_count}</td>
                      <td>
                        <small className="text-muted">
                          {result.claim_ids.slice(0, 3).join(', ')}
                          {result.claim_count > 3 && '...'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {results.queued_suggestions && results.queued_suggestions.length > 0 && (
            <div className="auto-added-section mt-4">
              <h4>Queued Lender Suggestions</h4>
              <div className="alert alert-success">
                <strong>Heads up!</strong> The following {results.queued_suggestions.length} lender variations were queued for review in the Suggestion Hub.
              </div>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Original Name</th>
                      <th>Suggested Canonical</th>
                      <th>Confidence</th>
                      <th>Reasoning</th>
                      <th>Queued At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.queued_suggestions.map((lender, idx) => (
                      <tr key={idx}>
                        <td>{lender.original}</td>
                        <td>{lender.matched || '—'}</td>
                        <td>{typeof lender.confidence === 'number' ? `${Math.round(lender.confidence * 100)}%` : '—'}</td>
                        <td>{lender.reasoning || '—'}</td>
                        <td>{new Date(lender.timestamp).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
